/**
 * Admin token manager — thin wrapper over the shared factory with
 * admin-specific storage keys plus an in-tab change event so the auth
 * store can react to writes from non-React code (axios refresh path).
 *
 * Cookie-based auth: both the access token and the rotating session live in
 * HttpOnly cookies the backend sets. localStorage holds only a non-sensitive
 * "has session" marker and a stable device id.
 */
import {
	createLocalStorageTokenManager,
	type TokenManager,
} from "@shared/http";
import { generateUuid, isValidUuid } from "@shared/utils";

const KEYS = {
	accessTokenKey: "finishd-admin-access-token",
	// Now holds a "1" session marker (not a token).
	refreshTokenKey: "finishd-admin-refresh-token",
} as const;
const DEVICE_ID_KEY = "finishd-admin-device-id";

export const ADMIN_AUTH_EVENT = "finishd-admin-auth-changed";

const base: TokenManager = createLocalStorageTokenManager(KEYS);

function notifyAuthChange(): void {
	if (typeof window !== "undefined") {
		window.dispatchEvent(new Event(ADMIN_AUTH_EVENT));
	}
}

export function getAccessToken(): string | null {
	return base.getAccessToken();
}

export function getRefreshToken(): string | null {
	return base.getRefreshToken();
}

/**
 * Stable per-install device id (survives logout) sent on verify-otp.
 *
 * `generateUuid`, not `crypto.randomUUID`. The latter exists only in a secure
 * context, so on a plain-HTTP LAN or Tailscale origin it is undefined and this
 * function threw -- before the verify request was ever built. Nothing reached
 * the backend, and the auth store rendered its catch-all "Admin access denied",
 * which reads as an authorization failure rather than a missing browser API.
 * The main frontend fixed this; the admin app never got the same treatment.
 */
export function getOrCreateDeviceId(): string {
	let id = localStorage.getItem(DEVICE_ID_KEY);
	// A non-UUID here is rejected by the backend with 400 (it now requires a
	// UUID `deviceId`), which would lock the operator out of login entirely.
	if (!id || !isValidUuid(id)) {
		id = generateUuid();
		localStorage.setItem(DEVICE_ID_KEY, id);
	}
	return id;
}

/**
 * Persist the device id the backend labelled this session with.
 *
 * The backend is authoritative and echoes the id it stored; reusing exactly
 * that value is what keeps repeat admin logins from one browser in a single
 * device entry (see #286).
 */
export function setDeviceId(id: string): void {
	localStorage.setItem(DEVICE_ID_KEY, id);
}

export function setTokens(): void {
	base.setTokens();
	notifyAuthChange();
}

export function clearTokens(): void {
	base.clearTokens();
	notifyAuthChange();
}

export const adminTokenManager: TokenManager = {
	getAccessToken: () => base.getAccessToken(),
	getRefreshToken: () => base.getRefreshToken(),
	setTokens: () => {
		base.setTokens();
		notifyAuthChange();
	},
	clearTokens: () => {
		base.clearTokens();
		notifyAuthChange();
	},
};
